import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, computed, ElementRef, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { AvatarModule, ButtonModule, TextareaModule } from '@sag-ui';
import { filter, fromEvent, Subject, tap } from 'rxjs';
import { IComment, IUser } from '@cod-monorepo/interfaces';
import { select, Store } from '@ngxs/store';
import { UserState } from '../../../user-management/state/user-management.state';
import { CodReviewState } from '../../state/cod-review.state';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import {
    AddComment,
    DeleteComment,
    DownloadAttachment,
    LazyLoadComments,
    LoadComments
} from '../../state/cod-review.action';
import { scrollOverAmountElement } from '../../../shared/utils/tiny-helpers';
import { CodAttachment } from '../../models/cod-create.model';
import { isAllowedFileType, MAX_FILE_SIZE_BYTES } from '@cod-monorepo/interfaces';

@Component({
    selector: 'app-cod-comments',
    templateUrl: './cod-comments.component.html',
    imports: [CommonModule, AvatarModule, TextareaModule, ButtonModule, ReactiveFormsModule],
    standalone: true
})
export class CodCommentsComponent implements AfterViewInit, OnDestroy, OnInit {
    protected user: Signal<IUser | undefined> = select(UserState.user);
    protected comments: Signal<IComment[]> = select(CodReviewState.comments);
    protected commentControl = new FormControl('', Validators.required);
    protected pendingAttachments: CodAttachment[] = [];
    protected canSubmit = false;

    @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

    private destroy$ = new Subject<void>();
    private commentsLoading: Signal<boolean> = select(CodReviewState.isLoadingComments);
    private lazyLoadingComments: Signal<boolean> = select(CodReviewState.isLazyLoadingComments);
    private allCommentsLoaded: Signal<boolean> = select(CodReviewState.allCommentsLoaded);

    @ViewChild('table', { static: true }) table!: ElementRef;

    public loading = computed(() => this.commentsLoading() || this.lazyLoadingComments());

    constructor(private store: Store) {}

    public ngOnInit(): void {
        this.loadComments();
    }

    public ngAfterViewInit(): void {
        this.initScrollListener();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public triggerFileInput(): void {
        this.fileInput.nativeElement.click();
    }

    public handleFileSelect(event: Event): void {
        const files = (event.target as HTMLInputElement).files;
        if (!files) return;
        Array.from(files)
            .filter(file => file.size <= MAX_FILE_SIZE_BYTES && isAllowedFileType(file.name, file.type || undefined))
            .forEach(file => {
                this.pendingAttachments = [...this.pendingAttachments, { name: file.name, file }];
            });
        this.fileInput.nativeElement.value = '';
    }

    public removePendingAttachment(index: number): void {
        this.pendingAttachments = this.pendingAttachments.filter((_, i) => i !== index);
    }

    public addComment(): void {
        if (!this.commentControl.value) {
            return;
        }

        this.store.dispatch(new AddComment(this.commentControl.value, [...this.pendingAttachments]));
        this.commentControl.reset();
        this.pendingAttachments = [];
        this.canSubmit = false;
    }

    public deleteComment(id: string): void {
        this.store.dispatch(new DeleteComment(id));
    }

    public downloadAttachment(key: string): void {
        this.store.dispatch(new DownloadAttachment(encodeURIComponent(key)));
    }

    public getFileName(key: string): string {
        return key.split('/').pop() || key;
    }

    private initScrollListener(): void {
        fromEvent<Event>(this.table.nativeElement, 'scroll')
            .pipe(
                filter(() => !this.lazyLoadingComments()),
                tap((event: Event) => {
                    if (!scrollOverAmountElement(event) || this.allCommentsLoaded()) {
                        return;
                    }

                    this.store.dispatch(new LazyLoadComments());
                })
            )
            .subscribe();
    }

    private loadComments(): void {
        this.store.dispatch(new LoadComments());
    }

    public handleInput(event: Event): void {
        this.canSubmit = !!(event.target as HTMLTextAreaElement).value;
    }
}
